import { useEffect, useState } from 'react';
import ZoomControl from './ZoomControl';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import SiteSelector from '@/components/SiteSelector';
import NotificationBell from '@/components/NotificationBell';
import MessageInboxBell from '@/components/MessageInboxBell';
import MechanicPerformanceAlertBanner from '@/components/MechanicPerformanceAlertBanner';
import GlobalSearch from '@/components/GlobalSearch';
import FloatingOpsWidget from '@/components/FloatingOpsWidget';
import { useFeatureFlags, invalidateFeatureFlags } from '@/hooks/useFeatureFlags';
import { useSubscription, invalidateSubscription } from '@/hooks/useSubscription';
import { clearSessionToken } from '@/lib/authFetch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Home,
  ClipboardList,
  Users,
  FileText,
  TrendingUp,
  Settings,
  AlertCircle,
  LogOut,
  Menu,
  X,
  Wrench,
  Calendar,
  Car,
  Mail,
  Package,
  BarChart3,
  DollarSign,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Layout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isDisabled } = useFeatureFlags();
  const { hasFeature } = useSubscription();

  // iter138 — Mechanics typically log in on iPads (10-11"). The full 256px
  // sidebar eats too much of the iPad's horizontal real estate, forcing
  // the user to zoom out and making everything look squished. For the
  // mechanic role we therefore render an icon-only sidebar at tablet
  // widths and only show the full-width version on true desktop (≥xl).
  // SM/FM/SA layout is untouched — they're happy on desktop monitors.
  const isMechanic = user?.role === 'mechanic';
  const sidebarWidthCls = isMechanic ? 'w-16 xl:w-64' : 'w-64';
  const mainPadCls = isMechanic
    ? 'px-3 sm:px-4 lg:px-6 xl:px-8 pt-3 xl:pt-4'
    : 'px-4 lg:px-8 pt-4';

  // iter156 — Poll the Comms unread-count endpoint so the sidebar shows
  // a red badge (1, 2, 3…) next to the "Comms" item whenever a customer
  // replies via SMS. SM asked for this ("SO SM KNOWS HE NEEDS TO CHECK").
  // Poll every 30s while any user with Comms access is logged in;
  // ping instantly when the tab regains focus so the badge is fresh
  // when the SM comes back from another window. Also silently absorbs
  // a 403 for roles that can't see Comms (mechanic / admin) — no toast.
  const canSeeComms = ['super_admin', 'admin', 'service_manager', 'floor_manager']
    .includes(user?.role);
  const [commsUnread, setCommsUnread] = useState(0);
  useEffect(() => {
    if (!canSeeComms) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/comms/inbox/unread-count`, {
          credentials: 'include',
        });
        if (!res.ok || cancelled) return;
        const d = await res.json();
        setCommsUnread(Number(d?.unread) || 0);
      } catch (_e) { /* silent — network blip, next poll retries */ }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
    };
  }, [canSeeComms]);

  const handleLogout = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      // Clear Safari/iOS fallback token regardless of API response —
      // we want the user logged out client-side even if the network
      // call failed. Otherwise authFetch would keep injecting Bearer.
      clearSessionToken();
      invalidateSubscription();
      invalidateFeatureFlags();
      if (response.ok) {
        toast.success('Logged out successfully');
        navigate('/login');
      } else {
        toast.error('Logout failed');
      }
    } catch (error) {
      console.error('Logout failed:', error);
      clearSessionToken();
      toast.error('Logout failed');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      service_manager: 'Service Manager',
      floor_manager: 'Floor Manager',
      mechanic: 'Mechanic / Technician',
      parts_staff: 'Parts Staff'
    };
    return roleMap[role] || role;
  };

  // Navigation items based on role
  const getNavItems = () => {
    // Mechanics get a focused 5-item menu only
    if (user?.role === 'mechanic') {
      return [
        { name: 'My Dashboard', icon: Home, path: '/my-workshop', roles: ['mechanic'], sub_key: 'home' },
        { name: 'Job Cards', icon: ClipboardList, path: '/job-cards', roles: ['mechanic'], sub_key: 'job_cards' },
        { name: 'Bonus / Incentive', icon: TrendingUp, path: '/my-workshop', roles: ['mechanic'], sub_key: 'incentives' },
        { name: 'Leave', icon: Calendar, path: '/leave', roles: ['mechanic'], sub_key: 'leave' },
        { name: 'Performance Review', icon: FileText, path: '/performance-review', roles: ['mechanic'], sub_key: 'performance' },
      ];
    }
    const items = [
      {
        name: 'Home',
        icon: Home,
        path: '/',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager', 'mechanic', 'parts_staff']
      },
      {
        name: 'Bookings',
        icon: Calendar,
        path: '/bookings',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager', 'mechanic', 'parts_staff']
      },
      {
        name: 'Job Cards',
        icon: ClipboardList,
        path: '/job-cards',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager', 'mechanic', 'parts_staff']
      },
      {
        name: 'Customers',
        icon: Users,
        path: '/customers',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager']
      },
      {
        name: 'Comms',
        icon: Mail,
        path: '/comms',
        sub_key: 'comms',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager']
      },
      {
        name: 'Vehicles',
        icon: Car,
        path: '/vehicles',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager']
      },
      {
        name: 'Invoices',
        icon: FileText,
        path: '/invoices',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager']
      },
      {
        name: 'Parts Catalog',
        icon: Package,
        path: '/parts-catalog',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager', 'parts_staff']
      },
      {
        name: 'Loan Cars',
        icon: Car,
        path: '/loan-cars',
        sub_key: 'loan_cars',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager']
      },
      {
        name: 'Reports',
        icon: TrendingUp,
        path: '/reports',
        roles: ['super_admin', 'admin'],
        feature_key: 'reports',
      },
      {
        name: 'Financials',
        icon: DollarSign,
        path: '/financials',
        sub_key: 'financials',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager']
      },
      {
        name: 'Accounts',
        icon: Wallet,
        path: '/accounts',
        sub_key: 'accounts',
        roles: ['super_admin', 'admin'],
        feature_key: 'accounts_receivable',
      },
      {
        name: 'Leave',
        icon: Calendar,
        path: '/leave',
        sub_key: 'leave',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager']
      },
      {
        name: 'Performance',
        icon: FileText,
        path: '/performance-review',
        sub_key: 'performance',
        roles: ['super_admin', 'admin', 'service_manager', 'floor_manager']
      },
      {
        name: 'Super Admin',
        icon: BarChart3,
        path: '/super-admin',
        roles: ['super_admin', 'admin']
      },
      {
        name: 'Marketing Centre',
        icon: Mail,
        path: '/marketing-centre',
        sub_key: 'marketing_centre',
        roles: ['super_admin', 'admin'],
        feature_key: 'marketing',
      },
      {
        name: 'Audit Logs',
        icon: AlertCircle,
        path: '/audit-logs',
        roles: ['super_admin']
      },
      {
        name: 'Settings',
        icon: Settings,
        path: '/settings',
        roles: ['super_admin', 'admin']
      }
    ];

    return items.filter(item => {
      if (!item.roles.includes(user?.role)) return false;
      if (item.feature_key && isDisabled(item.feature_key)) return false;
      // iter239 — workshop subscription tier gating (SA bypasses in hook)
      if (item.sub_key && !hasFeature(item.sub_key)) return false;
      return true;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 ${sidebarWidthCls} bg-slate-900 text-white transform transition-transform duration-200 flex flex-col
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
              <Wrench className="h-6 w-6 text-white" />
            </div>
            <span className={`font-['Chivo'] font-bold text-lg ${isMechanic ? 'hidden xl:inline' : ''}`}>AutoShopFlow</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-white hover:bg-slate-800"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {getNavItems().map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={`${item.name}-${item.path}`}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                onClick={() => {
                  if (item.target === '_blank') {
                    // Open kiosk-style links in a new tab so the SM can
                    // mount the iPad on the front counter independently
                    // of their main back-office workflow.
                    window.open(item.path, '_blank', 'noopener,noreferrer');
                    setSidebarOpen(false);
                    return;
                  }
                  if (item.path === location.pathname) return;
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors
                  ${isMechanic ? 'justify-center xl:justify-start' : ''}
                  ${isActive 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }
                `}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className={isMechanic ? 'hidden xl:inline' : ''}>{item.name}</span>
                {/* iter156 — Unread SMS badge on Comms. Pulses so the SM
                    catches it out of the corner of their eye. Hidden
                    when zero. Capped at "99+" so a rowdy weekend
                    doesn't burst the layout. */}
                {item.name === 'Comms' && commsUnread > 0 && (
                  <span
                    className={`
                      ml-auto inline-flex items-center justify-center
                      min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-black tabular-nums
                      ${isActive ? 'bg-white text-blue-700' : 'bg-rose-500 text-white'}
                      shadow-md ring-2 ring-slate-900 animate-pulse
                    `}
                    data-testid="comms-unread-badge"
                    title={`${commsUnread} unread customer message${commsUnread === 1 ? '' : 's'}`}
                  >
                    {commsUnread > 99 ? '99+' : commsUnread}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* iter138.9y — Zoom controls docked at the bottom of the sidebar
            per user request (was floating bottom-right and hiding the chat
            widget). Stays out of the way of any right-side workshop chat /
            notification overlays. Icon-only on mechanic iPads (collapsed
            sidebar). */}
        <div className={`px-3 pb-3 pt-2 border-t border-slate-800 ${isMechanic ? 'flex justify-center xl:justify-start' : ''}`}>
          <ZoomControl />
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-6 w-6" />
          </Button>

          {/* Global Search — takes the main space in header. Hidden for
              mechanics (iter235 — no confidential customer browsing). */}
          <div className="flex-1 max-w-2xl mx-4 hidden md:block">
            {!isMechanic && <GlobalSearch />}
          </div>

          <div className="flex-1 md:hidden" />

          {/* Site Selector (Super Admin only) */}
          <SiteSelector />

          {/* Customer-reply Message inbox (SM/FM/admin/SA only) */}
          {canSeeComms && hasFeature('comms') && <MessageInboxBell />}

          {/* Notification Bell */}
          <NotificationBell />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 hover:bg-slate-50 rounded-lg p-2 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-blue-600 text-white font-semibold">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
                  <div className="text-xs text-slate-500">{getRoleDisplay(user?.role)}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 pb-24 lg:pb-28">
          <div className={mainPadCls}>
            <MechanicPerformanceAlertBanner user={user} />
          </div>
          {children}
        </main>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Always-visible floating ops widget (pending jobs + productivity) */}
      <FloatingOpsWidget user={user} chatEnabled={hasFeature('pulse_chat')} />
    </div>
  );
}